import React, { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { FiSearch, FiClock, FiLayers, FiCpu, FiCheck } from "react-icons/fi";

const AutomationEditorModal = ({ isOpen, onClose, automation, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "published",
    targetEntities: ["predictions", "promises"],
    trigger: {
      type: "schedule",
      schedule: {
        interval: "day",
      },
    },
    searchConfig: {
      query: "կանխատեսում OR խոստանում եմ OR հայտարարել է",
      topic: "general",
      days: 7,
      maxResults: 15,
      searchDepth: "advanced",
      includeDomains: ["news.am", "armenpress.am", "azatutyun.am", "civilnet.am", "1lurer.am", "aravot.am", "pastv.am", "tert.am", "168.am"],
      excludeDomains: [],
    },
    autoDraft: false,
  });

  const [includeDomainsStr, setIncludeDomainsStr] = useState("");

  useEffect(() => {
    if (automation) {
      setFormData({
        name: automation.name || "",
        description: automation.description || "",
        status: automation.status || "published",
        targetEntities: automation.targetEntities || ["predictions", "promises"],
        trigger: automation.trigger || {
          type: "schedule",
          schedule: { interval: "day" },
        },
        searchConfig: {
          query: automation.searchConfig?.query || "կանխատեսում OR խոստանում եմ OR հայտարարել է",
          topic: automation.searchConfig?.topic || "general",
          days: automation.searchConfig?.days !== undefined ? automation.searchConfig.days : 7,
          maxResults: automation.searchConfig?.maxResults || 10,
          searchDepth: automation.searchConfig?.searchDepth || "advanced",
          includeDomains: automation.searchConfig?.includeDomains || [
            "news.am",
            "armenpress.am",
            "azatutyun.am",
            "civilnet.am",
            "1lurer.am",
          ],
          excludeDomains: automation.searchConfig?.excludeDomains || [],
        },
        autoDraft: !!automation.autoDraft,
      });
      setIncludeDomainsStr(
        (
          automation.searchConfig?.includeDomains || [
            "news.am",
            "armenpress.am",
            "azatutyun.am",
            "civilnet.am",
            "1lurer.am",
          ]
        ).join(", ")
      );
    } else {
      setFormData({
        name: "",
        description: "",
        status: "published",
        targetEntities: ["predictions", "promises"],
        trigger: {
          type: "schedule",
          schedule: { interval: "day" },
        },
        searchConfig: {
          query: "կանխատեսում OR խոստանում եմ OR հայտարարել է",
          topic: "general",
          days: 7,
          maxResults: 15,
          searchDepth: "advanced",
          includeDomains: [
            "news.am",
            "armenpress.am",
            "azatutyun.am",
            "civilnet.am",
            "1lurer.am",
          ],
          excludeDomains: [],
        },
        autoDraft: false,
      });
      setIncludeDomainsStr("news.am, armenpress.am, azatutyun.am, civilnet.am, 1lurer.am");
    }
  }, [automation, isOpen]);

  if (!isOpen) return null;

  const handleEntityToggle = (entity) => {
    setFormData((prev) => {
      const current = prev.targetEntities || [];
      const updated = current.includes(entity)
        ? current.filter((e) => e !== entity)
        : [...current, entity];
      return { ...prev, targetEntities: updated };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const domains = includeDomainsStr
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean);

    const payload = {
      ...formData,
      searchConfig: {
        ...formData.searchConfig,
        includeDomains: domains,
      },
    };

    onSave(payload, automation?.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl text-slate-800 shadow-2xl max-h-[90vh] flex flex-col my-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <span className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-200">
              <FiCpu size={20} />
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {automation ? "Խմբագրել ավտոմատացումը" : "Նոր AI ավտոմատացում"}
              </h2>
              <p className="text-xs text-slate-500">
                Կարգավորեք լրահոսի ավտոմատ որոնման և AI վերլուծության կանոնները
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition"
          >
            <IoClose size={22} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-5 flex-1">
          {/* Title & Description */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Ավտոմատացման անվանում *
              </label>
              <input
                type="text"
                required
                placeholder="օր. Կառավարության խոստումների օրական մոնիտորինգ"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Նկարագրություն
              </label>
              <textarea
                rows={2}
                placeholder="Հակիրճ նշեք, թե ինչ նպատակով է այս ավտոմատացումը..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-2 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
              />
            </div>
          </div>

          {/* Target Entities */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <FiLayers className="text-indigo-600" />
              Թիրախային սուբյեկտներ (ինչ դուրս բերել համացանցից)
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: "predictions", label: "Կանխատեսումներ" },
                { id: "promises", label: "Խոստումներ" },
                { id: "surveys", label: "Հարցումների թեմաներ" },
              ].map((ent) => {
                const isSelected = formData.targetEntities.includes(ent.id);
                return (
                  <button
                    key={ent.id}
                    type="button"
                    onClick={() => handleEntityToggle(ent.id)}
                    className={`flex items-center justify-between p-3 rounded-xl border text-left transition text-sm ${
                      isSelected
                        ? "bg-indigo-50 border-indigo-300 text-indigo-800 font-semibold"
                        : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300"
                    }`}
                  >
                    <span>{ent.label}</span>
                    {isSelected && <FiCheck className="text-indigo-600" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Trigger & Interval */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <FiClock className="text-indigo-600" />
                Գործարկման տեսակ
              </label>
              <select
                value={formData.trigger.type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    trigger: {
                      ...formData.trigger,
                      type: e.target.value,
                    },
                  })
                }
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              >
                <option value="schedule">Պարբերական ավտոմատ (Scheduled)</option>
                <option value="manual">Միայն ձեռքով (Manual Run)</option>
              </select>
            </div>

            {formData.trigger.type === "schedule" && (
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Գործարկման պարբերականություն
                </label>
                <select
                  value={formData.trigger.schedule?.interval || "day"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trigger: {
                        ...formData.trigger,
                        schedule: {
                          ...formData.trigger.schedule,
                          interval: e.target.value,
                        },
                      },
                    })
                  }
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                >
                  <option value="hour">Ամեն 1 ժամը մեկ (Hourly)</option>
                  <option value="6hours">Ամեն 6 ժամը մեկ</option>
                  <option value="12hours">Ամեն 12 ժամը մեկ</option>
                  <option value="day">Օրական 1 անգամ (Daily)</option>
                </select>
              </div>
            )}
          </div>

          {/* Web Search Config */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1.5">
              <FiSearch /> Համացանցային որոնման պարամետրեր (Tavily AI Search)
            </h3>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Որոնման բանալի բառեր (Search Query)
              </label>
              <input
                type="text"
                required
                value={formData.searchConfig.query}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    searchConfig: {
                      ...formData.searchConfig,
                      query: e.target.value,
                    },
                  })
                }
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Թույլատրելի կայքեր (Include Domains)
                </label>
                <input
                  type="text"
                  placeholder="news.am, armenpress.am, azatutyun.am"
                  value={includeDomainsStr}
                  onChange={(e) => setIncludeDomainsStr(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Ժամանակահատված (Թարմություն)
                </label>
                <select
                  value={formData.searchConfig.days !== undefined ? formData.searchConfig.days : 7}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      searchConfig: {
                        ...formData.searchConfig,
                        days: parseInt(e.target.value, 10),
                      },
                    })
                  }
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                >
                  <option value={1}>Վերջին 24 ժամ</option>
                  <option value={3}>Վերջին 3 օր</option>
                  <option value={7}>Վերջին 7 օր (Առաջարկվող)</option>
                  <option value={14}>Վերջին 14 օր</option>
                  <option value={30}>Վերջին 30 օր</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Առավելագույն հոդվածների քանակ
                </label>
                <select
                  value={formData.searchConfig.maxResults}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      searchConfig: {
                        ...formData.searchConfig,
                        maxResults: parseInt(e.target.value, 10),
                      },
                    })
                  }
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                >
                  <option value={5}>5 հոդված</option>
                  <option value={10}>10 հոդված</option>
                  <option value={15}>15 հոդված</option>
                  <option value={20}>20 հոդված</option>
                </select>
              </div>
            </div>
          </div>

          {/* Auto-Draft & Status */}
          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer select-none bg-slate-50 p-3 rounded-xl border border-slate-200">
              <input
                type="checkbox"
                checked={formData.autoDraft}
                onChange={(e) =>
                  setFormData({ ...formData, autoDraft: e.target.checked })
                }
                className="w-4 h-4 mt-0.5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
              />
              <div>
                <span className="text-sm font-semibold text-slate-900 block">
                  Ավտոմատ ստեղծել «Սևագիր» (Pending) նախագծեր
                </span>
                <span className="text-xs text-slate-500 block mt-0.5">
                  Գտնված կանխատեսումներն ու խոստումները անմիջապես կստեղծվեն predictions/promises բաժնում (չհրապարակված վիճակում)
                </span>
              </div>
            </label>

            <div className="pt-2">
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Կարգավիճակ
              </label>
              <div className="flex gap-3">
                {[
                  { id: "published", label: "Ակտիվ (Published)" },
                  { id: "paused", label: "Կասեցված (Paused)" },
                  { id: "draft", label: "Սևագիր (Draft)" },
                ].map((st) => (
                  <button
                    key={st.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, status: st.id })}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold border transition ${
                      formData.status === st.id
                        ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                        : "bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {st.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition"
            >
              Չեղարկել
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md shadow-indigo-200 transition"
            >
              {automation ? "Պահպանել" : "Ստեղծել ավտոմատացում"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutomationEditorModal;
